import { useCallback } from 'react';

import { useModal } from '@farmshare/ui-components';

import { EnumUserType, User } from '../../../../lib/graphql';

import { CustomerForm } from '../customer-form/customer-form';

export interface AddCustomerModalOptions {
  /** Existing customer to edit; omit to create a new one. */
  customer?: User | null;
  /** Pin the relationship type (e.g. Producer) instead of letting the user pick. */
  forcedType?: EnumUserType;
  /** Called with the created/updated customer after a successful save. */
  onSuccess?: (created: User) => void | Promise<void> | undefined;
  /** Header text. Defaults to "Add Customer". */
  title?: string;
  vendorId?: string;
}

/**
 * Returns a function that opens the add/edit customer modal.
 *
 * `info` rather than `save`: the form inside is `CustomerForm`, which owns its
 * own Formik instance and its own Save/Cancel row, so the modal contributes the
 * chrome only — hence `footer: null`, which suppresses the default Ok button.
 * @returns A callback that opens the modal.
 */
export function useAddCustomerModal() {
  const { info } = useModal();

  return useCallback(
    ({
      customer = null,
      forcedType,
      onSuccess,
      title = 'Add Customer',
      vendorId,
    }: AddCustomerModalOptions = {}) => {
      info({
        type: 'info',
        title,
        size: 'sm',
        // A full customer form (name, contact, address, hang-time fields) is
        // unusable in a centered card on a phone. Fullscreen there is already
        // the `info` default (see IModalBase.fullscreenOnMobile); stated here
        // because this modal depends on it.
        fullscreenOnMobile: true,
        // CustomerForm renders its own Save/Cancel; null suppresses the Ok
        // button the `info` type would otherwise add below them.
        footer: null,
        // No aria-label needed: the shared renderer renders the title as an
        // <h2> and labels the dialog by it, as this dialog's own markup used to.
        // Function body, not a plain node, so the form's Cancel and post-save
        // close run the renderer's own close (which animates out) rather than
        // the provider's `destroy` (which yanks the dialog off-screen).
        body: (modalProps) => (
          <CustomerForm
            customer={customer}
            forcedType={forcedType}
            onSuccess={onSuccess}
            onCancel={() => modalProps.onClose?.()}
            onClose={() => modalProps.onClose?.()}
            // The form runs its own create/update, which the modal can't see.
            setBusy={(busy) => modalProps.setBusy?.(busy)}
            isInDialog
            showActions
            idPrefix="add-customer"
            vendorId={vendorId}
          />
        ),
      });
    },
    [info],
  );
}
